import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Text,
  Card,
  CardBody,
  Badge,
  Flex,
  Icon,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Link,
  HStack,
  VStack,
  Divider,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Textarea,
  useToast
} from '@chakra-ui/react';
import { FaCrown, FaPhone, FaEnvelope, FaGlobe, FaSearch, FaPaperPlane, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { supplierService } from '../../../services/supplierService';
import { authService } from '../../../services/authService';

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [emailData, setEmailData] = useState({ subject: '', body: '' });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const popularBadgeBg = useColorModeValue('yellow.100', 'yellow.900');
  const popularBadgeColor = useColorModeValue('yellow.800', 'yellow.200');
  const canManageSupplierAgreement = authService.isManager();

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const response = await supplierService.getSuppliers();
      setSuppliers(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Tedarikçiler yüklenirken hata:', err);
      setError('Tedarikçiler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Filtrelenmiş tedarikçiler
  const filteredSuppliers = useMemo(() => {
    if (!searchQuery.trim()) {
      return suppliers;
    }
    
    const query = searchQuery.toLowerCase().trim();
    return suppliers.filter(supplier => {
      const nameMatch = supplier.name?.toLowerCase().includes(query);
      const contactMatch = supplier.contactPerson?.toLowerCase().includes(query);
      const phoneMatch = supplier.phone?.toLowerCase().includes(query);
      const emailMatch = supplier.email?.toLowerCase().includes(query);
      const serviceMatch = supplier.services?.some(service => 
        service.toLowerCase().includes(query)
      );
      
      return nameMatch || contactMatch || phoneMatch || emailMatch || serviceMatch;
    });
  }, [suppliers, searchQuery]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      {/* Uyarı Notu */}
      <Alert status="info" borderRadius="md" mb={6}>
        <AlertIcon />
        <Box>
          <Text fontWeight="bold" fontSize="sm" mb={2}>
            Önemli Bilgilendirme
          </Text>
          <Text fontSize="xs" mb={2}>
            Menom, tedarikçilerle yapılan anlaşmaları yalnızca işletme bildirimi üzerinden takip edebilmektedir.
          </Text>
          <Text fontSize="xs" mb={2}>
            <strong>"Bu Tedarikçiyle Anlaştım"</strong> butonuna basmanız:
          </Text>
          <VStack align="start" spacing={1} ml={4} mb={2}>
            <Text fontSize="xs">• Anlaşmanızın resmî olarak sistemde kayıt altına alınmasını</Text>
            <Text fontSize="xs">• Menom'un tedarikçi tarafında süreci sahiplenebilmesini</Text>
            <Text fontSize="xs">• Size özel Menom avantajlarının aktif edilmesini sağlar</Text>
          </VStack>
          <Text fontSize="xs" fontWeight="bold" color="red.500">
            ⚠️ Bildirilmeyen anlaşmalar Menom kapsamında değerlendirilemez.
          </Text>
        </Box>
      </Alert>

      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
        <InputGroup maxW={{ base: "100%", md: "300px" }}>
          <InputLeftElement pointerEvents="none">
            <Icon as={FaSearch} color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Tedarikçi ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg={cardBg}
            borderColor={borderColor}
          />
        </InputGroup>
      </Flex>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
          <Spinner size="xl" />
        </Box>
      ) : error ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      ) : filteredSuppliers.length === 0 ? (
        <Card bg={cardBg} borderRadius="xl" border="1px solid" borderColor={borderColor}>
          <CardBody p={8}>
            <Text textAlign="center" color="gray.500">
              {searchQuery ? 'Arama sonucu bulunamadı.' : 'Henüz tedarikçi bulunmamaktadır.'}
            </Text>
          </CardBody>
        </Card>
      ) : (
        <VStack spacing={4} align="stretch">
          {filteredSuppliers.map((supplier) => (
            <Card
              key={supplier._id}
              bg={cardBg}
              borderRadius="xl"
              border="1px solid"
              borderColor={borderColor}
              boxShadow="sm"
              _hover={{
                boxShadow: 'md',
                transform: 'translateX(4px)',
                transition: 'all 0.2s'
              }}
            >
              <CardBody p={6}>
                <Flex
                  direction={{ base: 'column', md: 'row' }}
                  gap={6}
                  align={{ base: 'stretch', md: 'center' }}
                >
                  {/* Sol Taraf: Temel Bilgiler */}
                  <Flex direction="column" flex={1} gap={3}>
                    <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
                      <Text fontSize="xl" fontWeight="bold" color={textColor}>
                        {supplier.name}
                      </Text>
                      {supplier.isPopular && (
                        <Badge
                          bg={popularBadgeBg}
                          color={popularBadgeColor}
                          px={2}
                          py={1}
                          borderRadius="md"
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <Icon as={FaCrown} />
                          Popüler
                        </Badge>
                      )}
                    </Flex>

                    {/* Menom Üzerinden İletişime Geç Butonu - BÜYÜK CTA */}
                    {supplier.email && !supplier.hasAgreement && (
                      <Box>
                        <Button
                          leftIcon={<FaPaperPlane />}
                          colorScheme="blue"
                          size="md"
                          w="100%"
                          onClick={() => {
                            setSelectedSupplier(supplier);
                            setEmailData({
                              subject: `Menom Üzerinden İletişim - ${supplier.name}`,
                              body: `Merhaba ${supplier.contactPerson || supplier.name},\n\nMenom platformu üzerinden sizinle iletişime geçmek istiyorum.\n\nDetaylı bilgi için lütfen iletişime geçiniz.\n\nSaygılarımla`
                            });
                            onOpen();
                          }}
                          mb={2}
                        >
                          Menom Üzerinden İletişime Geç
                        </Button>
                        <Text fontSize="xs" color="gray.500" textAlign="center" mb={2}>
                          Avantajlardan yararlanmak için önerilen iletişim yolu
                        </Text>
                      </Box>
                    )}
                    
                    {/* Anlaşma: yalnızca yönetici (işletme sahibi veya manager çalışan); staff butonları görmez */}
                    {canManageSupplierAgreement ? (
                      supplier.hasAgreement ? (
                        <>
                          <Button
                            leftIcon={<FaCheckCircle />}
                            colorScheme="green"
                            size="md"
                            w="100%"
                            variant="outline"
                            onClick={async () => {
                              try {
                                await supplierService.cancelAgreement(supplier._id);
                                toast({
                                  title: 'Anlaşma iptal edildi',
                                  description: `${supplier.name} ile anlaşma durumu kaldırıldı`,
                                  status: 'info',
                                  duration: 3000,
                                  isClosable: true
                                });
                                fetchSuppliers();
                              } catch (error) {
                                toast({
                                  title: 'Hata',
                                  description: error.response?.data?.message || 'Anlaşma iptal edilirken bir hata oluştu',
                                  status: 'error',
                                  duration: 3000,
                                  isClosable: true
                                });
                              }
                            }}
                            mb={3}
                          >
                            Anlaşma İptal Et
                          </Button>
                          {supplier.agreementDate && (
                            <Text fontSize="xs" color="green.600" textAlign="center" mb={3} fontWeight="semibold">
                              ✓ {new Date(supplier.agreementDate).toLocaleDateString('tr-TR')} tarihinde anlaştınız
                            </Text>
                          )}
                        </>
                      ) : (
                        <Button
                          leftIcon={<FaCheckCircle />}
                          colorScheme="green"
                          size="md"
                          w="100%"
                          variant="outline"
                          onClick={async () => {
                            try {
                              await supplierService.agreeWithSupplier(supplier._id);
                              toast({
                                title: 'Anlaşma kaydedildi',
                                description: `${supplier.name} ile anlaştığınız işaretlendi`,
                                status: 'success',
                                duration: 3000,
                                isClosable: true
                              });
                              fetchSuppliers();
                            } catch (error) {
                              toast({
                                title: 'Hata',
                                description: error.response?.data?.message || 'Anlaşma kaydedilirken bir hata oluştu',
                                status: 'error',
                                duration: 3000,
                                isClosable: true
                              });
                            }
                          }}
                          mb={3}
                        >
                          Bu Tedarikçiyle Anlaştım
                        </Button>
                      )
                    ) : (
                      supplier.hasAgreement &&
                      supplier.agreementDate && (
                        <Text fontSize="xs" color="gray.500" textAlign="center" mb={3}>
                          Bu tedarikçi ile işletme anlaşması kayıtlı (yalnızca yönetici değiştirebilir).
                        </Text>
                      )
                    )}

                    {/* İletişim Bilgileri - Küçük ve Blur */}
                    <Flex direction={{ base: 'column', sm: 'row' }} gap={4} flexWrap="wrap">
                      {supplier.contactPerson && (
                        <Text fontSize="sm" color="gray.600">
                          <strong>İletişim:</strong> {supplier.contactPerson}
                        </Text>
                      )}
                      
                      {supplier.phone && (
                        <HStack>
                          <Icon as={FaPhone} color="gray.400" fontSize="xs" />
                          <Text 
                            fontSize="xs" 
                            color="gray.400"
                            filter="blur(0.5px)"
                            opacity={0.6}
                            as={Link}
                            href={`tel:${supplier.phone}`}
                          >
                            {supplier.phone}
                          </Text>
                        </HStack>
                      )}

                      {supplier.email && (
                        <HStack>
                          <Icon as={FaEnvelope} color="gray.400" fontSize="xs" />
                          <Text 
                            fontSize="xs" 
                            color="gray.400"
                            filter="blur(0.5px)"
                            opacity={0.6}
                          >
                            {supplier.email}
                          </Text>
                        </HStack>
                      )}

                      {supplier.website && (
                        <HStack>
                          <Icon as={FaGlobe} color="gray.400" fontSize="xs" />
                          <Link
                            href={supplier.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            fontSize="xs"
                            color="gray.400"
                            isTruncated
                            maxW="200px"
                          >
                            {supplier.website}
                          </Link>
                        </HStack>
                      )}
                    </Flex>
                  </Flex>

                  {/* Sağ Taraf: Hizmetler ve Notlar */}
                  <Flex direction="column" gap={3} minW={{ base: '100%', md: '200px' }}>
                    {/* Hizmetler */}
                    {supplier.services && supplier.services.length > 0 && (
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.600">
                          Hizmetler:
                        </Text>
                        <Flex wrap="wrap" gap={2}>
                          {supplier.services.map((service, index) => (
                            <Badge key={index} colorScheme="blue" variant="subtle" fontSize="xs">
                              {service}
                            </Badge>
                          ))}
                        </Flex>
                      </Box>
                    )}

                    {/* Notlar */}
                    {supplier.notes && (
                      <Text fontSize="sm" color="gray.600" fontStyle="italic" noOfLines={3}>
                        {supplier.notes}
                      </Text>
                    )}
                  </Flex>
                </Flex>
              </CardBody>
            </Card>
          ))}
        </VStack>
      )}

      {/* Mail Gönderme Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Menom Üzerinden İletişime Geç - {selectedSupplier?.name}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Box>
                  <Text fontSize="sm" fontWeight="bold">Önemli:</Text>
                  <Text fontSize="xs">
                    Bu mail Menom platformu üzerinden gönderilecek ve info@menomsoft.com adresine CC olarak iletilecektir. 
                    Referans numaranız otomatik olarak oluşturulacaktır.
                  </Text>
                </Box>
              </Alert>

              <FormControl>
                <FormLabel>Konu</FormLabel>
                <Input
                  value={emailData.subject}
                  onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                  placeholder="Mail konusu"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Mesaj</FormLabel>
                <Textarea
                  value={emailData.body}
                  onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
                  placeholder="Tedarikçiye göndermek istediğiniz mesaj"
                  rows={8}
                />
              </FormControl>

              {selectedSupplier && (
                <Box w="100%" p={3} bg="gray.50" borderRadius="md">
                  <Text fontSize="xs" fontWeight="bold" mb={2}>Alıcı:</Text>
                  <Text fontSize="sm">{selectedSupplier.email}</Text>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    CC: info@menomsoft.com
                  </Text>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              İptal
            </Button>
            <Button
              colorScheme="blue"
              leftIcon={<FaPaperPlane />}
              onClick={async () => {
                try {
                  await supplierService.sendSupplierEmail(selectedSupplier._id, emailData);
                  toast({
                    title: 'Mail gönderildi',
                    description: 'Tedarikçiye mail başarıyla gönderildi. Referans numaranız oluşturuldu.',
                    status: 'success',
                    duration: 5000,
                    isClosable: true
                  });
                  onClose();
                  setEmailData({ subject: '', body: '' });
                  setSelectedSupplier(null);
                } catch (error) {
                  toast({
                    title: 'Hata',
                    description: error.response?.data?.message || 'Mail gönderilirken bir hata oluştu',
                    status: 'error',
                    duration: 5000,
                    isClosable: true
                  });
                }
              }}
            >
              Mail Gönder
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

